import { useState, useEffect } from 'react';

import {
  HsFormFields,
  HubspotApiData,
  HubspotApiStatus,
  HubspotDataType,
  HubspotData,
} from '~app/pages/Pricing/components/HubSpotForm/types';

const EXCLUDE_COUNTRY_FIELD = 'contact_country__c';
const HUBSPOT_PROXY = '/cc/hs/';
const HUBSPOT_FORM_ID = '289edeb2-bb6b-45f7-bef3-7185f3c6e9f4';

/**
 * Given the ID of the form to be displayed, fetches Hubspot JSON data from the API
 * @param formId  The ID of the form to be displayed
 * @returns Promise<HubspotApiData>   Status, error, form data
 */
const fetchHubSpotForm = async (formId = HUBSPOT_FORM_ID): Promise<HubspotApiData> => {
  const formData = new URLSearchParams();
  formData.append('formId', formId);
  const fetchHSForms = await fetch(HUBSPOT_PROXY, {
    method: 'POST',
    body: formData,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
  return fetchHSForms.json();
};

/**
 * If the field name is 'contact_country__c', hide the field,
 * otherwise return the value of the hidden flag associated with the field.
 * @param {param0} name, hidden
 * @returns boolean Whether or not the field should be hidden
 */
function mapHiddenField({ name, hidden }: HubspotDataType): boolean {
  return name === EXCLUDE_COUNTRY_FIELD ? true : hidden;
}

/**
 * Function used in useHubSpotFormAPI; takes the values returned from the API
 * and transforms them into the format required for the front end.
 * @param hsData {T extends HubspotDataType}
 * @returns HubspotData
 */
function mapHubSpotFormFields<T extends HubspotDataType>(hsData: T): HubspotData {
  const { form } = hsData;
  const mappedData: HubspotData = {
    fields: [],
    redirect: form?.redirect,
    guid: form?.guid,
    portalId: form?.portalId,
  };

  const shallowCopiedFormFields: HubspotDataType[] = [...form?.formFieldGroups];

  const temp = shallowCopiedFormFields.map((field: HubspotDataType) => {
    return field?.fields.map((formFields: HubspotDataType): HsFormFields => {
      return {
        id: `HubSpotForm__${formFields.name}`,
        type: formFields.type,
        name: formFields.name,
        defaultValue: formFields.defaultValue,
        label: formFields.label,
        fieldType: formFields.fieldType,
        required: formFields.required,
        hidden: mapHiddenField(formFields),
        hideLabel: formFields.labelHidden,
        placeholder: formFields.placeholder,
      };
    });
  });
  return { ...mappedData, fields: temp.flat() };
}

/**
 * Used directly in the HubspotForm component.
 * Calls the function to fetch the data from the API
 * and then returns either the formatted data or an error.
 * @param {string}  formId  The ID of the form to be fetched
 * @returns form data
 */
export default function useHubSpotFormAPI<T extends Partial<T>>(formId = HUBSPOT_FORM_ID): T | {} {
  const [data, setData] = useState<T | {}>({
    status: HubspotApiStatus.Loading,
    error: null,
    data: null,
  });
  useEffect(() => {
    let isMounted = true;
    fetchHubSpotForm(formId)
      .then((formData: HubspotDataType) => {
        if (isMounted) {
          setData({
            data: mapHubSpotFormFields(formData),
            error: null,
            status: 'Success',
          });
        }
        return () => {
          isMounted = false;
        };
      })
      .catch(err => {
        setData({
          data: null,
          status: HubspotApiStatus.Error,
          error: err,
        });
      });
  }, [formId]);

  return data;
}
